import { Component, effect, input, output, signal, untracked } from '@angular/core';
import { Driver, FleetClient, MergeFleetRequest, Vehicle } from '../../models/client';
import { AppModule } from '../../app.module';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { ClientService } from '../../shared/client.service';
import { AuthService } from '../../auth.service';
import { NotificationService } from '../../shared/notification.service';
import { PriceTemplate } from '../../models/pricing';
import { SharedDataService } from '../../shared/shareddata.service';
import { WhatsappService } from '../../whatsapp/whatsapp.service';
import { WaGroupLinkDirective } from '../../shared/wa-group-link.directive';

@Component({
  selector: 'app-fleet-management-edit',
  imports: [AppModule, WaGroupLinkDirective],
  templateUrl: './fleet-management-edit.html',
  styleUrl: './fleet-management-edit.less'
})
export class FleetManagementEdit {

  selectedFleet = input.required<FleetClient | null>();
  isVisible = input.required<boolean>();
  additionalFleets = input<FleetClient[]>([]);

  visibleChange = output<boolean>();
  fleetSaved = output<FleetClient>();
  fleetUpdated = output<FleetClient>();
  fleetsMerged = output<FleetClient[]>();
  isLoading = signal<boolean>(false);
  isLoadingGroupName = signal<boolean>(false);
  pricingTemplates = signal<PriceTemplate[]>([]);

  form = new FormGroup({
    clientName: new FormControl('', [Validators.required, Validators.minLength(3)]),
    clientTel: new FormControl('', [ Validators.pattern(/^\+?[0-9\s\-]{7,15}$/)]),
    operatorName: new FormControl('', [ Validators.minLength(3)]),
    operatorCell: new FormControl('', [ Validators.pattern(/^\+?[0-9\s\-]{7,15}$/)]),
    pricingId: new FormControl(0, [Validators.required]),
    whatsappGroupLink: new FormControl('', [ Validators.pattern(/^(https?:\/\/)?(www\.)?chat\.whatsapp\.com\/.+$/)]),
    whatsappGroupName: new FormControl('')
  });

  drivers = signal<Driver[]>([]);
  newDriverForm = new FormGroup({
    name: new FormControl('', [Validators.required, Validators.minLength(3)]),
    phone: new FormControl('', [Validators.required, Validators.pattern(/^\+?[0-9\s\-]{7,15}$/)])
  });

  vehicles = signal<Vehicle[]>([]);
  newVehicleForm = new FormGroup({
    registration: new FormControl('', [Validators.required, Validators.minLength(3)]),
    vin: new FormControl('')
  });

  constructor(private authService: AuthService, private clientService: ClientService, private notification: NotificationService, private sharedData: SharedDataService, private whatsappService: WhatsappService) {

    effect(() => {
      if (this.isVisible()) {
        this.populateForm();
      }
    });

  }

  private populateForm() {
    this.pricingTemplates.set(this.sharedData.priceTemplates)
    const fleet = this.selectedFleet();
    if (fleet) {
      this.form.patchValue({
        clientName: fleet.clientName,
        clientTel: fleet.clientTel,
        operatorName: fleet.operatorName,
        operatorCell: fleet.operatorCell,
        pricingId: fleet.pricingId || 0,
        whatsappGroupLink: fleet.whatsappGroupLink || '',
        whatsappGroupName: fleet.whatsappGroupName || ''
      });
      this.drivers.set(fleet.drivers || []);
      this.vehicles.set(fleet.vehicles || []);
      for (const additionalFleet of untracked(() => this.additionalFleets() || [])) { 
          this.drivers.update(drivers => [...drivers, ...(additionalFleet.drivers || [])]);
          this.vehicles.update(vehicles => [...vehicles, ...(additionalFleet.vehicles || [])]);
        }

    }
  }

  public handleSave() {
    if (this.form.invalid) {
      this.notification.showError('Please fix the errors in the form before saving'); 
      return;
    }

    const rawValues = this.form.getRawValue();
    const updatedFleet: FleetClient = {
      clientId: this.selectedFleet()?.clientId || 0,
      clientCell: rawValues.clientTel! || '',
      pricingId:  rawValues.pricingId || 0,
      clientName: rawValues.clientName!,
      clientTel: rawValues.clientTel!,
      operatorName: rawValues.operatorName!,
      operatorCell: rawValues.operatorCell!,
      drivers: this.drivers(),
      vehicles: this.vehicles(),
      connectionTypeId: this.selectedFleet()?.connectionTypeId || 2, // Default to fleet connection
      latitude: this.selectedFleet()?.latitude || 0,
      longitude: this.selectedFleet()?.longitude || 0,
      whatsappGroupLink: rawValues.whatsappGroupLink || null,
      whatsappGroupName: rawValues.whatsappGroupName || null
    };
    this.clientService.upsertFleet(this.authService.user!.clientId, updatedFleet).subscribe({
      next: (updatedFleet: FleetClient) => {
        this.fleetSaved.emit(updatedFleet);
        this.notification.showSuccess('Fleet saved successfully');
        this.form.reset();
        this.vehicles.set([]);
        this.drivers.set([]);
      },
      error: (error) => {
        this.notification.showError('Error saving fleet');
        console.error('Error saving fleet:', error);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });

      }

  public onGroupNameResolved(name: string): void {
    this.form.get('whatsappGroupName')?.setValue(name);
    this.form.get('whatsappGroupName')?.enable();
  }

  public onGroupLoadingChange(loading: boolean): void {
    this.isLoadingGroupName.set(loading);
    if (loading) {
      this.form.get('whatsappGroupName')?.setValue('');
      this.form.get('whatsappGroupName')?.disable();
    }
  }

  public handleCancel() {
    this.form.reset();
    this.vehicles.set([]);
    this.drivers.set([]);
    this.visibleChange.emit(false);
  }

  public isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.dirty || field.touched));
  }

  public getFieldErrorMessage(fieldName: string): string {
    const field = this.form.get(fieldName);
    if (!field || !field.errors) return '';
    
    if (field.errors['required']) return 'This field is required';
    if (field.errors['minlength']) return `Minimum ${field.errors['minlength'].requiredLength} characters required`;
    if (field.errors['pattern']) return 'Invalid format';
    if (field.errors['min']) return `Minimum value is ${field.errors['min'].min}`;
    if (field.errors['max']) return `Maximum value is ${field.errors['max'].max}`;
    
    return 'Invalid value';
  }

  public removeDriver(driverId: number) {
    this.isLoading.set(true);
    this.clientService.removeDriver(this.selectedFleet()!.clientId, driverId).subscribe({
      next: () => {
        this.drivers.update(drivers => drivers.filter(d => d.driverId !== driverId));
        this.fleetUpdated.emit({...this.selectedFleet()!, drivers: this.drivers()});
       // this.handleSave();
      },
      error: (error) => {
        this.notification.showError('Error removing driver');
        console.error('Error removing driver:', error);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });
  }

  public addDriver() {
    const driver = new Driver();
    driver.name = this.newDriverForm.value.name!;
    driver.phoneNumber = this.newDriverForm.value.phone!;
    this.isLoading.set(true);
    this.clientService.addFleetTruckDriver(this.selectedFleet()!.clientId,driver).subscribe({
      next: (driver: Driver) => {
        this.drivers.update(drivers => [...drivers, driver]);
        this.newDriverForm.reset();
        this.fleetUpdated.emit({...this.selectedFleet()!, drivers: this.drivers()});
       // this.handleSave();
      },
      error: (error) => {
        this.notification.showError('Error adding driver');
        console.error('Error adding driver:', error);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });

  }
  
  public removeVehicle(vehicleId: number) {
    this.isLoading.set(true);
    console.log('Removing vehicle with ID:', vehicleId);
    this.clientService.removeVehicle(this.selectedFleet()!.clientId, vehicleId).subscribe({
      next: () => {
        this.vehicles.update(vehicles => vehicles.filter(v => v.vehicleId !== vehicleId));
        this.fleetUpdated.emit({...this.selectedFleet()!, vehicles: this.vehicles()});
      },
      error: (error) => {
        this.notification.showError('Error removing vehicle');
        console.error('Error removing vehicle:', error);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });
  }

  public addVehicle() {
    this.isLoading.set(true);
    const vehicle = new Vehicle();
    vehicle.registrationNumber = this.newVehicleForm.value.registration!;
    vehicle.vinNumber = this.newVehicleForm.value.vin!;
    this.clientService.addFleetVehicle(this.selectedFleet()!.clientId, vehicle).subscribe({
      next: (vehicle: Vehicle) => {
        this.vehicles.update(vehicles => [...vehicles, vehicle]);
        this.newVehicleForm.reset();
        this.fleetUpdated.emit({...this.selectedFleet()!, vehicles: this.vehicles()});
      },
      error: (error) => {
        this.notification.showError('Error adding vehicle');
        console.error('Error adding vehicle:', error);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });
  }

  public handleMerge() {
    if (this.additionalFleets().length === 0) {
      this.notification.showError('Please select at least one additional fleet to merge');
      return;
    }
    const targetFleet = this.selectedFleet();
    if (!targetFleet) {
      this.notification.showError('Target fleet not found');
      return;
    }
    const sourceFleetIds = this.additionalFleets().map(f => f.clientId);

    const request = new MergeFleetRequest();
    request.targetClient = targetFleet;
    request.sourceClientIds = sourceFleetIds;
    this.clientService.mergeFleets(request).subscribe({
      next: (mergedFleet: FleetClient) => {
        this.fleetSaved.emit(mergedFleet);
        this.fleetsMerged.emit(this.additionalFleets());
        console.log('Merged fleet:', this.additionalFleets());
        this.notification.showSuccess('Fleets merged successfully');
        this.form.reset();
        this.vehicles.set([]);
        this.drivers.set([]);
      },
      error: (error) => {
        this.notification.showError('Error merging fleets');
        console.error('Error merging fleets:', error);
      },
      complete: () => {
        this.isLoading.set(false);
      }
    });

  }
}
